import React, { useEffect, useState } from 'react';
import { fixOrder, replaceById } from '@ax2/common/helpers/array';
import {
  deleteImage,
  fetchImage,
  storeSurvey,
  uploadImage,
} from 'rest/makeSurveyClient';
import FileUpload from 'components/file-dnd/FileUpload';
import PreviewFile from 'components/file-dnd/PreviewFile';
import {
  useSurveyContext,
  useSurveyDispatchContext,
} from '../../contexts/SurveyContext';
import { QuestionBaseContentData } from 'components/question-builders/QuestionContent';
import ModalButton from '@ax2/common/components/ModalButton';
import style from '@/styles/components/file-dnd/ImageUploadModal.module.css';
import { ActionButton } from '@ax2/common/components/ActionButton';
import ImageData from '@ax2/common/model/schemas/ImageDataSchema';
import { uuid } from '@ax2/common/model/UUID';

const ImageUploadModal: React.FC<QuestionBaseContentData> = props => {
  const [selectedFile, setSelectedFile] = useState<any | null>(null);
  const [imgUpload, setImgUpload] = useState<ImageData | null>(null);
  const [imgDelete, setImgDelete] = useState<boolean>(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const surveySchema = useSurveyContext();
  const surveyDispach = useSurveyDispatchContext();

  useEffect(() => {
    if (surveySchema && imgUpload) {
      const questions = replaceById(surveySchema.questions, {
        ...props.question,
        imageUrl: imgUpload,
      });
      surveyDispach({ type: 'questions', questions: questions });
      storeSurvey(
        {
          ...surveySchema,
          questions: fixOrder(questions),
        },
        true
      );
    }
  }, [imgUpload]);

  useEffect(() => {
    if (surveySchema && imgDelete) {
      const questions = replaceById(surveySchema.questions, {
        ...props.question,
        imageUrl: null,
      });
      surveyDispach({ type: 'questions', questions: questions });
      storeSurvey(
        {
          ...surveySchema,
          questions: fixOrder(questions),
        },
        true
      );
    }
  }, [imgDelete]);

  const handleUpload = () => {
    if (selectedFile) {
      const size = selectedFile.size;
      const type = selectedFile.type;
      if (size <= 1024 * 1024 * 5 && type.includes('image/')) {
        setLoading(true);
        try {
          const name = selectedFile.name.trim();
          const key = `${uuid()}-${name.substring(name - 30)}`;
          uploadImage(key, selectedFile).then(res => {
            if (res === 'OK') {
              setImgUpload({ Key: key, fileName: name });
              setImgDelete(false);
              setLoading(false);
            }
          });
        } catch {
          console.log('error whle uploading image');
          setLoading(false);
        }
      }
    }
  };

  const handleDelete = async () => {
    if (
      props.question &&
      props.question.imageUrl &&
      props.question.imageUrl.Key
    ) {
      setLoading(true);
      try {
        const imgKey = props.question.imageUrl.Key.replace(/^.*?\//, '');
        deleteImage(imgKey).then(res => {
          if (res === 'ok') {
            setImgDelete(true);
            setImageSrc(null);
            setLoading(false);
            setImgUpload(null);
          }
        });
      } catch {
        console.log('error whle deleting image');
        setLoading(false);
      }
    }
  };

  const handleFileUpload = async (file: File | null) => {
    setSelectedFile(file);
  };

  React.useEffect(() => {
    async function fetchImageData() {
      if (
        props.question.imageUrl &&
        props.question.imageUrl.Key &&
        !imgDelete &&
        modalOpen
      ) {
        try {
          const imgKey = props.question.imageUrl.Key.replace(/^.*?\//, '');
          const imgData = await fetchImage(imgKey);
          const imgUrlData = URL.createObjectURL(imgData);
          setImageSrc(imgUrlData);
        } catch (error) {
          console.log('Error fetching image Data', error);
        }
      }
    }
    fetchImageData();
  }, [props.question.imageUrl, imgDelete, modalOpen]);

  return (
    <div className={style.buttonWrapper}>
      <ModalButton
        disabled={props.question.questionType === 'Empty'}
        key={props.question.id + 'uploadimg'}
        text="Upload Image"
        content={(_, modal) => {
          if (modal) setModalOpen(modal);
          return (
            <div className={style.modalContainer}>
              <h4 className={style.modalHeading}>Upload file to images</h4>
              {!(props.question.imageUrl && props.question.imageUrl.Key) ? (
                <FileUpload
                  onFileUpload={handleFileUpload}
                  selectedFile={selectedFile}
                />
              ) : null}
              {selectedFile ? <p>Selected file: {selectedFile.name}</p> : null}
              <div className={style.modalButtonsGroup}>
                {selectedFile && !imgUpload ? (
                  <>
                    <ActionButton
                      text="Upload"
                      type="primary"
                      size="normal"
                      disabled={!selectedFile || loading}
                      onClick={handleUpload}
                      loading={loading}
                    />
                    &nbsp;&nbsp;
                    <ActionButton
                      text="Clear"
                      type="primaryAlt"
                      size="normal"
                      disabled={!selectedFile || loading}
                      onClick={() => handleFileUpload(null)}
                    />
                  </>
                ) : null}
                {props.question.imageUrl && props.question.imageUrl.Key ? (
                  <ActionButton
                    text="Delete"
                    type="primaryAlt"
                    size="normal"
                    disabled={!imageSrc || loading}
                    onClick={handleDelete}
                    loading={loading}
                  />
                ) : null}
              </div>
              {(props.question.imageUrl && props.question.imageUrl.Key) ||
              selectedFile ? (
                <PreviewFile
                  fileData={selectedFile}
                  setFileData={setSelectedFile}
                  imageSrc={imageSrc}
                />
              ) : null}
            </div>
          );
        }}
      />
    </div>
  );
};

export default ImageUploadModal;
